import { useState, useEffect, useCallback } from "react";
import { Badge, Card, CardBody, Col, Input, Row, Spinner, Table } from "reactstrap";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultPagination from "../../../../components/Pagination";
import { useTranslation } from "react-i18next";
import { get } from "../../../../utils/helpers/api";

const ACTION_COLORS = {
  created:  'success',
  updated:  'primary',
  deleted:  'danger',
  exported: 'warning',
  login:    'secondary',
  logout:   'secondary',
};

const MODULES = ['crm', 'sales', 'purchases', 'inventory', 'accounting', 'hr', 'settings'];

const PAGE_SIZE = 20;

function AuditLogsPage() {
  const { t } = useTranslation();
  const [logs, setLogs]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState('');
  const [actionFilter, setAction]   = useState('');
  const [moduleFilter, setModule]   = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const [page, setPage]             = useState(1);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
    if (search)       params.append('search',    search);
    if (actionFilter) params.append('action',    actionFilter);
    if (moduleFilter) params.append('module',    moduleFilter);
    if (dateFrom)     params.append('date_from', dateFrom);
    if (dateTo)       params.append('date_to',   dateTo);

    const data = await get(`/settings/audit-logs?${params.toString()}`);
    setLoading(false);

    if (data?.success) {
      setLogs(data.logs.data);
      setTotal(data.logs.total);
      setTotalPages(data.logs.last_page);
    }
  }, [page, search, actionFilter, moduleFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('settings.auditLog')} />

        <div className="subheader-page-content scrollable">
          <p className="text-muted mb-4">{t('settings.fullHistory')}</p>

          {/* Filters */}
          <Card className="mb-3">
            <CardBody>
              <Row className="g-2">
                <Col lg="4">
                  <Input
                    placeholder={t('settings.searchLogsPlaceholder')}
                    value={search}
                    onChange={handleFilter(setSearch)}
                  />
                </Col>
                <Col lg="2">
                  <Input type="select" value={actionFilter} onChange={handleFilter(setAction)}>
                    <option value="">{t('settings.allActions')}</option>
                    {Object.keys(ACTION_COLORS).map(a => (
                      <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>
                    ))}
                  </Input>
                </Col>
                <Col lg="2">
                  <Input type="select" value={moduleFilter} onChange={handleFilter(setModule)}>
                    <option value="">{t('settings.allModules')}</option>
                    {MODULES.map(m => (
                      <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                    ))}
                  </Input>
                </Col>
                <Col lg="2">
                  <Input type="date" value={dateFrom} onChange={handleFilter(setDateFrom)} />
                </Col>
                <Col lg="2">
                  <Input type="date" value={dateTo} onChange={handleFilter(setDateTo)} />
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Table */}
          <Card>
            <CardBody className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner size="sm" /> {t('common.loading')}
                </div>
              ) : (
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-3">{t('settings.timestamp')}</th>
                      <th>{t('common.name')}</th>
                      <th>{t('settings.action')}</th>
                      <th>{t('settings.module')}</th>
                      <th>{t('common.description')}</th>
                      <th>{t('settings.ipAddress')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-5">
                          {t('settings.noLogsFound')}
                        </td>
                      </tr>
                    ) : logs.map(log => (
                      <tr key={log.id}>
                        <td className="ps-3 text-muted small text-nowrap">
                          { new Date(log.created_at).toLocaleString() }
                        </td>
                        <td className="fw-medium text-nowrap">{log.user?.firstname + ' ' + log.user?.lastname}</td>
                        <td>
                          <Badge color={ACTION_COLORS[log.action] ?? 'secondary'} pill>
                            {log.action.charAt(0).toUpperCase() + log.action.slice(1)}
                          </Badge>
                        </td>
                        <td className="text-muted">
                          {log.module.charAt(0).toUpperCase() + log.module.slice(1)}
                        </td>
                        <td>{log.description}</td>
                        <td className="text-muted small">{log.ip}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted">{total} result{total !== 1 ? 's' : ''}</small>
            <DefaultPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>

        </div>
      </div>
    </SidebarLayout>
  );
}

export default AuditLogsPage;
